import { checkIfFileExists } from '@hashnode/file-parsing'
import path from 'path'
import {
	EnhancedGuideInformation,
	EnhancedGuideInformationWithPageContent,
	FileErrorMap,
	GuideFileErrorMap,
	MissingFile,
	ParsedPageContentConfigWithFilePath,
	VersionFileErrorMap,
} from '../../types'
import { getSidebarFilePaths } from '../get-sidebar-paths'
import { checkReferencedFiles } from '../linking'

function checkPageContent(page: ParsedPageContentConfigWithFilePath) {
	const missingFiles = checkReferencedFiles({
		content: page.content || '',
		folderPath: path.dirname(page.filePath),
		numberOfMatterLines: page.numberOfMatterLines ?? 0,
	})

	return {
		filePath: page.filePath,
		missingFiles,
	}
}

export function checkMissingFilesInSidebar(
	guideInformationWithAllPages: EnhancedGuideInformation[],
): GuideFileErrorMap<MissingFile> {
	const missingFiles: GuideFileErrorMap<MissingFile> = new Map()

	guideInformationWithAllPages.forEach((guide) => {
		const missingFilesPerVersion: Map<string, Map<string, MissingFile[]>> = new Map()

		const missingFilesForSidebarConfig: MissingFile[] = []
		guide.versionInformation.forEach((version) => {
			if ('sidebar' in version.config) {
				const allSidebarPaths = getSidebarFilePaths({
					versionConfig: version.config,
					versionFolderPath: version.versionFolderPath,
				})

				allSidebarPaths.forEach((sidebarItem) => {
					if (!checkIfFileExists(sidebarItem)) {
						missingFilesForSidebarConfig.push({
							missingFilePath: sidebarItem,
						})
					}
				})
				if (missingFilesForSidebarConfig.length > 0) {
					missingFilesPerVersion.set(
						version.versionFolderPath,
						new Map<string, MissingFile[]>([
							[`${version.versionFolderPath}/config.ts`, missingFilesForSidebarConfig],
						]),
					)
				}
			}
			if (missingFilesPerVersion.size > 0) {
				missingFiles.set(guide.guideFolderPath, missingFilesPerVersion)
			}
		})
	})

	return missingFiles
}

export function checkMissingReferences(
	guides: EnhancedGuideInformationWithPageContent[],
): GuideFileErrorMap<MissingFile> {
	const missingFilesPerGuide: GuideFileErrorMap<MissingFile> = new Map()

	guides.forEach((guide) => {
		const missingFilesPerVersion: VersionFileErrorMap<MissingFile> = new Map()

		guide.versionInformation.forEach((version) => {
			const missingFilesPerPage: FileErrorMap<MissingFile> = new Map()

			version.existingFiles.forEach((page) => {
				const result = checkPageContent(page)
				if (result && result.missingFiles.length > 0) {
					missingFilesPerPage.set(page.filePath, result.missingFiles)
				}
			})
			missingFilesPerVersion.set(version.versionFolderPath, missingFilesPerPage)
		})

		missingFilesPerGuide.set(guide.guideFolderPath, missingFilesPerVersion)
	})

	return missingFilesPerGuide
}
